import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useAuth } from "../../hooks/useAuth";
import { useSharePoint } from "../../hooks/useSharePoint";
import {
  Card,
  Typography,
  Input,
  Select,
  Alert,
  Row,
  Col,
  Button,
  Modal,
  List,
  Tag,
  Divider,
  Space,
  Spin,
  message,
} from "antd";
import {
  ArrowRightOutlined,
  CalendarOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useUserRole } from "../../hooks/useUserRole";
import MainLayout from "../layout/MainLayout";
import { REGIONS, COLORS } from "../../utils/constants";
import {
  getAllSavedProjects,
  transformFromPayload,
  prepareTenderForAPI,
  saveTenderToStorage,
} from "../../utils/storage";
import { billOfQuantitiesApi } from "../../api/billOfQuantities";
import { itemLibraryApi } from "../../api/itemLibrary";
import { useOpportunities } from "../../hooks/useOpportunities";
import { useCreateBillOfQuantities } from "../../hooks/useBillOfQuantities";
import { TenderState } from "../../types/tender.types";
import ImportedOpportunityDisplay from "./ImportedOpportunityDisplay";

// Basic tender information form data
interface BasicTenderFormData {
  projectName: string;
  client: string;
  siteAddress: string;
  region: string;
}

interface OpportunityRecord {
  key: string;
  no?: string;
  tenderId: string;
  tender: string;
  client: string;
  siteAddress?: string;
  region?: string;
  totalBudget: number;
  rsBudget: number;
  gwBudget: number;
  creationDate: string;
  lastUpdateDate: string;
  status:
    | "Pending"
    | "Submitted"
    | "Approved"
    | "Won"
    | "Lost"
    | "In Progress"
    | string;
  creator: string;
  closed?: boolean;
}

const { Title, Text } = Typography;

// Memoized List Item Component for better performance
const OpportunityListItem = React.memo<{
  project: OpportunityRecord;
  isSelected: boolean;
  onSelect: (tenderId: string) => void;
}>(({ project, isSelected, onSelect }) => {
  const getStatusColor = (status: string) => {
    const statusLower = (status || "").toLowerCase().trim();

    // New BOQ workflow statuses (matching dashboard)
    if (statusLower === "in-progress") return "blue";
    if (statusLower === "under review - senior estimator") return "orange";
    if (statusLower === "under review commercial director") return "gold";
    if (statusLower === "under client review") return "green";

    // Legacy statuses
    switch (statusLower) {
      case "approved":
        return "success";
      case "submitted":
        return "processing";
      case "pending":
        return "warning";
      case "won":
        return "success";
      case "lost":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusStyle = (status: string) => {
    return { fontWeight: 600, fontSize: "13px" };
  };

  const getStatusDisplay = (status: string) => {
    const normalizeStatus = (status: string) => {
      const statusLower = status.toLowerCase();
      if (statusLower === "draft") return "In-progress";
      return statusLower;
    };
    const toPascalCase = (str: string) => {
      return str
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    };
    const statusNormalized = normalizeStatus(status);
    return toPascalCase(statusNormalized);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <List.Item
      className={`cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
        isSelected ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-gray-50"
      }`}
      style={{ padding: "12px 16px" }}
      onClick={() => onSelect(project.tenderId)}
    >
      <List.Item.Meta
        title={
          <div className="flex items-center gap-2 mb-1">
            <Text strong className="text-gray-900">
              {project.tender || "Unnamed Project"}
            </Text>
            
          </div>
        }
        description={
          <div>
            {project.no && (
              <Text className="text-gray-500 block mb-1 text-xs">
                Opportunity No: {project.no}
              </Text>
            )}
            <Text className="text-gray-500 block mb-1 text-xs">
              Client: {project.client || "Not specified"}
            </Text>
            {project.siteAddress && (
              <Text className="text-gray-500 block mb-1 text-xs">
                Site: {project.siteAddress}
              </Text>
            )}
            {project.region && (
              <Text className="text-gray-500 block mb-1 text-xs">
                Region: {project.region}
              </Text>
            )}
            <Text className="text-gray-500 block mb-1 text-xs">
              Created: {formatDate(project.creationDate)} • Modified:{" "}
              {formatDate(project.lastUpdateDate)}
            </Text>
          </div>
        }
      />
    </List.Item>
  );
});

OpportunityListItem.displayName = "OpportunityListItem";

const TenderInfo: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state, updateField, dispatch } = useTender();
  const { saveStatus } = useAutoSave();
  const { user } = useAuth();
  const { role, region: userRegion, isLoading: isRoleLoading } = useUserRole();
  const { createFolder, isCreatingFolder } = useSharePoint();
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [projectSelectModalVisible, setProjectSelectModalVisible] =
    useState(false);
  const [opportunities, setOpportunities] = useState<OpportunityRecord[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [hasLocalStorageData, setHasLocalStorageData] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [focusedBtn, setFocusedBtn] = useState<string | null>(null);
  const [isImportedData, setIsImportedData] = useState(false);
  const [importedOpportunity, setImportedOpportunity] =
    useState<OpportunityRecord | null>(null);

  const { data: bcOpportunities, isLoading: isOpportunitiesLoading } = useOpportunities();

  // Mutation for creating/submitting BOQ
  const createBoqMutation = useCreateBillOfQuantities();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<BasicTenderFormData>({
    mode: "onBlur",
    defaultValues: {
      projectName: state.projectName || "",
      client: state.client,
      siteAddress: state.siteAddress || "",
      region: state.region,
    },
  });

  const watchedFields = watch();

  // Ref to track if we've already handled the initial page load/reload
  const hasCheckedReload = useRef(false);

  // Clear cache on page reload
  useEffect(() => {
    // Only check once on initial mount
    if (hasCheckedReload.current) {
      return;
    }

    const isPageReload = () => {
      // Check if this is a page reload using Performance API
      const navEntries = performance.getEntriesByType(
        "navigation",
      ) as PerformanceNavigationTiming[];
      if (navEntries.length > 0) {
        return navEntries[0].type === "reload";
      }
      // Fallback for older browsers
      return performance.navigation && performance.navigation.type === 1;
    };

    if (isPageReload()) {
      console.log("Page reload detected - clearing cache");

      // Clear all tender-related localStorage items
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith("mjEvans_tender_") ||
            key === "currentTender")
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      // Reset form to initial state
      setValue("projectName", "");
      setValue("client", "");
      setValue("siteAddress", "");
      setValue(
        "region",
        userRegion && userRegion !== "All Regions" ? userRegion : "",
      );

      // Reset imported data state
      setIsImportedData(false);
      setImportedOpportunity(null);
      setOpportunities([]);
      setValidationErrors([]);

      // Clear any error messages
      setApiError(null);

      // Reset TenderContext to initial state
      dispatch({
        type: "RESET_TENDER",
      });
    }

    // Mark that we've checked for reload
    hasCheckedReload.current = true;
  }, [dispatch, setValue, userRegion]); // Added dependencies

  // Set createdBy when component mounts if not already set
  useEffect(() => {
    if (!state.createdBy && user) {
      updateField("createdBy", user.name || user.email || "User");
    }
  }, [state.createdBy, user, updateField]);

  // Track if we're currently updating from form (to prevent circular updates)
  const isUpdatingFromForm = useRef(false);

  // Auto-save with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isValid && Object.keys(errors).length === 0) {
        // Set flag to indicate we're updating context from form
        isUpdatingFromForm.current = true;
        updateFormData(watchedFields);
        validateProjectName(watchedFields.projectName);
        // Reset flag after a short delay
        setTimeout(() => {
          isUpdatingFromForm.current = false;
        }, 100);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [watchedFields, isValid]);

  // Track previous state values to detect real changes (not just re-renders)
  const prevStateRef = useRef({
    projectName: state.projectName,
    client: state.client,
    siteAddress: state.siteAddress,
    region: state.region,
  });

  // Update form when context state changes (but only for meaningful changes)
  useEffect(() => {
    // Don't sync if we're currently updating context from form (prevents circular updates)
    if (isUpdatingFromForm.current) {
      return;
    }

    const prev = prevStateRef.current;
    const current = {
      projectName: state.projectName,
      client: state.client,
      siteAddress: state.siteAddress,
      region: state.region,
    };

    // Check if there's an actual change in values
    const hasChanged =
      prev.projectName !== current.projectName ||
      prev.client !== current.client ||
      prev.siteAddress !== current.siteAddress ||
      prev.region !== current.region;

    // Only update form if there's a real change from external source (import/load)
    if (hasChanged) {
      setValue("projectName", current.projectName, { shouldValidate: false });
      setValue("client", current.client, { shouldValidate: false });
      setValue("siteAddress", current.siteAddress || "", {
        shouldValidate: false,
      });
      setValue("region", current.region, { shouldValidate: false });

      // Update ref with new values
      prevStateRef.current = current;
    }
  }, [
    state.projectName,
    state.client,
    state.siteAddress,
    state.region,
    setValue,
  ]);

  // Set region from user profile if a specific region is selected
  // If "All Regions" is selected, show "Select Region" placeholder
  useEffect(() => {
    if (userRegion && userRegion !== "All Regions") {
      setValue("region", userRegion);
      updateField("region", userRegion);
    } else if (userRegion === "All Regions") {
      // Clear region to show "Select Region" placeholder
      setValue("region", "");
      updateField("region", "");
    }
  }, [userRegion, setValue, updateField]);

  const updateFormData = (data: BasicTenderFormData) => {
    updateField("projectName", data.projectName.trim());
    updateField("client", data.client.trim());
    updateField("siteAddress", (data.siteAddress || "").trim());
    updateField("region", data.region);
  };

  // Helper function to calculate budgets from tender data (fallback)
  const calculateBudgets = (tenderData: TenderState) => {
    let rsBudget = 0;
    let gwBudget = 0;

    // Calculate Roads & Sewers Budget
    if (tenderData.preliminaryItems?.totals?.grandTotal) {
      rsBudget = tenderData.preliminaryItems.totals.grandTotal;
    }

    // Calculate Groundworks Budget
    if (tenderData.groundworks) {
      // Main groundworks total
      if (tenderData.groundworks.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.totals.grandTotal;
      }

      // External works total
      if (tenderData.groundworks.externalWorks?.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.externalWorks.totals.grandTotal;
      }

      // Private drainage total
      if (tenderData.groundworks.privateDrainage?.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.privateDrainage.totals.grandTotal;
      }

      // Additional items total
      if (tenderData.groundworks.additionalItems?.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.additionalItems.totals.grandTotal;
      }
    }

    const totalBudget = rsBudget + gwBudget;

    return { totalBudget, rsBudget, gwBudget };
  };

  // Check if local storage data exists on mount
  useEffect(() => {
    const checkLocalStorage = () => {
      try {
        let hasValidData = false;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("mjEvans_tender_")) {
            // Also check if the key has actual data
            const data = localStorage.getItem(key);
            if (data && data !== "null" && data !== "undefined") {
              try {
                const parsed = JSON.parse(data);
                // Verify it has valid tender data
                if (parsed && (parsed.data || parsed.projectName)) {
                  hasValidData = true;
                  break;
                }
              } catch (parseError) {
                console.error(`Invalid data in key ${key}:`, parseError);
              }
            }
          }
        }
        setHasLocalStorageData(hasValidData);
      } catch (error) {
        console.error("Error checking local storage:", error);
        setHasLocalStorageData(false);
      }
    };

    checkLocalStorage();
  }, []);

  // When BC opportunities data arrives while the modal is already open, populate the list
  useEffect(() => {
    if (projectSelectModalVisible && bcOpportunities && bcOpportunities.length > 0) {
      setOpportunities(bcOpportunities as OpportunityRecord[]);
    }
  }, [bcOpportunities, projectSelectModalVisible]);

  const loadOpportunities = () => {
    try {
      let savedOpportunities: OpportunityRecord[] = [];
      setApiError(null);
      setOpportunitiesLoading(true);

      // Use React Query cached data (already fetched by useOpportunities hook)
      if (bcOpportunities && bcOpportunities.length > 0) {
        savedOpportunities = bcOpportunities as OpportunityRecord[];
      }

      // Fallback: Use localStorage if no BC API data
      if (savedOpportunities.length === 0) {
        const projects = getAllSavedProjects();

        savedOpportunities = projects.map((project) => {
          const storageKey = `mjEvans_tender_${project.id}`;
          const storedData = localStorage.getItem(storageKey);

          let totalBudget = 0;
          let rsBudget = 0;
          let gwBudget = 0;
          let status = "In-progress";
          let creator = "Unknown";
          let lastModified = project.lastModified.toISOString();
          let createdDate = project.dateCreated.toISOString();

          if (storedData) {
            try {
              const parsed = JSON.parse(storedData);
              const tenderData: TenderState = parsed.data || parsed;
              const budgets = calculateBudgets(tenderData);
              totalBudget = budgets.totalBudget;
              rsBudget = budgets.rsBudget;
              gwBudget = budgets.gwBudget;

              // Extract status, creator, and dates from stored data if available
              if (tenderData.status) {
                status = tenderData.status;
              }
              if ((tenderData as any).createdBy) {
                creator = (tenderData as any).createdBy;
              }
              if (tenderData.lastModified) {
                lastModified = tenderData.lastModified.toISOString
                  ? tenderData.lastModified.toISOString()
                  : (tenderData.lastModified as any);
              }
              if (tenderData.dateCreated) {
                createdDate = tenderData.dateCreated.toISOString
                  ? tenderData.dateCreated.toISOString()
                  : (tenderData.dateCreated as any);
              }
            } catch (error) {
              console.error(`Error parsing tender data:`, error);
            }
          }

          return {
            key: `tender_${project.id}`,
            tenderId: project.id,
            tender: project.projectName,
            client: project.client || "-",
            siteAddress: project.siteAddress || "",
            region: project.region || "",
            totalBudget,
            rsBudget,
            gwBudget,
            creationDate: createdDate,
            lastUpdateDate: lastModified,
            status: status,
            creator,
          };
        });
      }

      setOpportunities(savedOpportunities);
    } catch (error) {
      console.error("Error loading opportunities:", error);
      setOpportunities([]);
    } finally {
      setOpportunitiesLoading(false); // Stop loading
    }
  };

  const handleOpenProjectSelect = () => {
    if (opportunities.length === 0 && !isOpportunitiesLoading) {
      loadOpportunities();
    }
    setProjectSelectModalVisible(true);
  };

  const handleImportProject = async (record: OpportunityRecord) => {
    if (isImporting) return; // prevent concurrent calls from rapid clicks
    setIsImporting(true);
    const storageKey = `mjEvans_tender_${record.tenderId}`;

    try {
      // Check if a BOQ already exists for this tender name.
      // performImport always saves using record.tender as the projectName,
      // so the duplicate lookup must use the same key.
      let isDuplicate = false;
      let existingBoqStatus = "";

      try {
        const boqResponse = await billOfQuantitiesApi.getByProject(record.tender, record.no);

        // The API can return the document at the top level, or wrapped in
        // { data: ... } / { project: ... }.  Any non-empty successful response
        // means a BOQ already exists for this project.
        const responseIsPresent =
          boqResponse &&
          boqResponse.success !== false &&
          typeof boqResponse === "object" &&
          Object.keys(boqResponse).length > 0;

        if (responseIsPresent) {
          isDuplicate = true;
          existingBoqStatus =
            boqResponse.data?.status ||
            boqResponse.project?.status ||
            boqResponse.status ||
            "Unknown";
        }
      } catch (apiError: any) {
        // 404 means no existing project — not a duplicate
        if (apiError?.response?.status !== 404) {
          console.warn("Error checking for duplicate BOQ:", apiError);
        }
      }

      // If duplicate found, show confirm dialog and navigate to version history
      if (isDuplicate) {
        let proceedClicked = false;
        Modal.confirm({
          title: "Client details aleady imported",
          content: (
            <div>
              <p>This opportunity already has imported client details.</p>
              {existingBoqStatus && (
                <p style={{ marginTop: "8px" }}>
                  <strong>Status:</strong>{" "}
                  <Tag color={getStatusColor(existingBoqStatus)}>
                    {getStatusDisplay(existingBoqStatus)}
                  </Tag>
                </p>
              )}
              <p style={{ marginTop: "12px" }}>Click below to view details.</p>
            </div>
          ),
          okText: "Proceed to Tender",
          cancelText: "Cancel",
          onOk: () => {
            if (proceedClicked) return;
            proceedClicked = true;
            navigate(`/version-history/${encodeURIComponent(record.tender)}${record.no ? `?oppNo=${encodeURIComponent(record.no)}` : ''}`);
          },
          onCancel: () => {},
        });
        return;
      }

      // No duplicate, proceed with import
      await performImport(record, storageKey);
    } catch (error) {
      console.error("Error importing opportunity:", error);
      message.error({
        content: "Failed to import opportunity",
        key: "loadTender",
        duration: 3,
      });
    } finally {
      // Always re-enable the button — handles early returns (e.g. duplicate path)
      // as well as any error path not covered by performImport's own finally.
      setIsImporting(false);
    }
  };

  const performImport = async (
    record: OpportunityRecord,
    storageKey: string,
  ) => {
    try {
      message.loading({
        content: "Loading opportunity data...",
        key: "loadTender",
        duration: 0,
      });

      // Use region from opportunity if available, otherwise use current state or default
      const regionToUse = record.region || state.region || REGIONS[0];

      setValue("projectName", record.tender);
      setValue("client", record.client);
      setValue("siteAddress", record.siteAddress || "");
      setValue("region", regionToUse);

      // Update context with basic data
      updateField("projectName", record.tender);
      updateField("client", record.client);
      updateField("siteAddress", record.siteAddress || "");
      updateField("region", regionToUse);
      updateField("opportunityNo", record.no || "");

      // Try to fetch full tender data if it exists
      try {
        const response = await billOfQuantitiesApi.getByProject(record.tender, record.no);
        let apiData = null;

        // Extract data based on response structure
        if (response && response.success !== false) {
          if (response.data) {
            apiData = response.data;
          } else if (response.project) {
            apiData = response;
          } else if (
            response &&
            typeof response === "object" &&
            Object.keys(response).length > 0
          ) {
            apiData = response;
          }
        }

        // If full tender data exists, load it
        if (apiData) {
          const tenderState = transformFromPayload(apiData, {
            id: record.tenderId,
          });

          // Preserve project name from opportunity
          if (record.tender) {
            tenderState.projectName = record.tender;
          }

          // Preserve client from opportunity
          if (record.client) {
            tenderState.client = record.client;
          }

          // Preserve site address from opportunity
          if (record.siteAddress) {
            tenderState.siteAddress = record.siteAddress;
          }

          // Preserve region from opportunity
          if (record.region) {
            tenderState.region = record.region;
          }

          // Always preserve the BC opportunity number from the selected record
          if (record.no) {
            tenderState.opportunityNo = record.no;
          }

          // Check if there's actual data in the imported project
          let hasRoadsAndSewersData = false;
          let hasGroundworksData = false;

          if (
            apiData.roadAndSewers ||
            apiData.preliminaryItems ||
            apiData.foulSewers ||
            apiData.stormSewers ||
            apiData.roads ||
            apiData.mainServices
          ) {
            hasRoadsAndSewersData = true;
          }

          if (
            apiData.groundworks ||
            (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
            (apiData.foundations && Object.keys(apiData.foundations).length > 0)
          ) {
            hasGroundworksData = true;
          }

          tenderState.jobTypes = {
            roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0,
            groundworks: hasGroundworksData || record.gwBudget > 0,
          };

          // Update TenderContext with full data
          dispatch({ type: "LOAD_PROJECT", payload: tenderState });

          // Explicitly update form fields with data from loaded tender (especially siteAddress)
          setValue("projectName", tenderState.projectName);
          setValue("client", tenderState.client);
          setValue("siteAddress", tenderState.siteAddress || "");
          setValue("region", tenderState.region);

          // Save to localStorage
          const dataToStore = {
            version: "3.0",
            timestamp: new Date().toISOString(),
            data: apiData,
          };

          localStorage.setItem(storageKey, JSON.stringify(dataToStore));
          localStorage.setItem("currentTender", JSON.stringify(dataToStore));

          message.success({
            content: "Opportunity imported with full tender data",
            key: "loadTender",
            duration: 2,
          });
        } else {
          // No full tender data, but basic fields are already populated
          message.success({
            content: "Opportunity details imported successfully",
            key: "loadTender",
            duration: 2,
          });
        }
      } catch (apiError) {
        // Failed to fetch full data, but basic fields are already populated
        message.success({
          content: "Opportunity details imported successfully",
          key: "loadTender",
          duration: 2,
        });
      }

      // Mark as imported data and store the opportunity info
      setIsImportedData(true);
      setImportedOpportunity(record);

      // Close the modal
      setProjectSelectModalVisible(false);
      setSelectedProjectId(null);
      setSearchQuery("");

      // Trigger form validation
      setTimeout(() => {
        trigger();
      }, 100);
    } catch (error) {
      console.error("Error in performImport:", error);
      message.error({
        content: "Failed to import opportunity",
        key: "loadTender",
        duration: 3,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancelProjectSelect = () => {
    setProjectSelectModalVisible(false);
    setSelectedProjectId(null);
    setSearchQuery("");
    setIsImporting(false);
  };

  const handleCreateNewTender = () => {
    // Reset to form mode
    setIsImportedData(false);
    setImportedOpportunity(null);

    // Clear form fields
    setValue("projectName", "");
    setValue("client", "");
    setValue("siteAddress", "");
    setValue("region", REGIONS[0]);

    // Reset context
    updateField("projectName", "");
    updateField("client", "");
    updateField("siteAddress", "");

    message.info("Ready to create a new tender");
  };

  const handleLoadLocalProject = () => {
    try {
      message.loading({
        content: "Loading from local storage...",
        key: "loadLocal",
        duration: 0,
      });

      // Find the first (and only) stored project
      let foundKey = null;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("mjEvans_tender_")) {
          foundKey = key;
          break;
        }
      }

      if (!foundKey) {
        message.error({
          content: "No locally saved project found",
          key: "loadLocal",
          duration: 3,
        });
        return;
      }

      const savedData = localStorage.getItem(foundKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);

        // Extract the payload data (same format as API response)
        const payloadData = parsedData.data || parsedData;

        // Extract project ID from key
        const projectId = foundKey.replace("mjEvans_tender_", "");

        // Transform from PayloadFormat to TenderState (same as API import)
        const tenderState = transformFromPayload(payloadData, {
          id: projectId,
        });

        // Check for actual data and set job types
        let hasRoadsAndSewersData = false;
        let hasGroundworksData = false;

        if (
          payloadData.roadAndSewers ||
          payloadData.preliminaryItems ||
          payloadData.foulSewers ||
          payloadData.stormSewers ||
          payloadData.roads ||
          payloadData.mainServices
        ) {
          hasRoadsAndSewersData = true;
        }

        if (
          payloadData.groundworks ||
          (payloadData.cutFills &&
            Object.keys(payloadData.cutFills).length > 0) ||
          (payloadData.foundations &&
            Object.keys(payloadData.foundations).length > 0)
        ) {
          hasGroundworksData = true;
        }

        // Calculate budgets for display
        const budgets = calculateBudgets(tenderState);

        tenderState.jobTypes = {
          roadsAndSewers: hasRoadsAndSewersData || budgets.rsBudget > 0,
          groundworks: hasGroundworksData || budgets.gwBudget > 0,
        };

        // Load the data into context
        dispatch({ type: "LOAD_PROJECT", payload: tenderState });

        message.success({
          content: `Loaded project: ${tenderState.projectName}`,
          key: "loadLocal",
          duration: 3,
        });

        // Trigger form validation to enable Save & Continue button
        setTimeout(() => {
          trigger();
        }, 100);
      } else {
        message.error({
          content: "Project data not found in local storage",
          key: "loadLocal",
          duration: 3,
        });
      }
    } catch (error) {
      console.error("Error loading local project:", error);
      message.error({
        content: "Failed to load project from local storage",
        key: "loadLocal",
        duration: 3,
      });
    }
  };

  const validateProjectName = (projectName: string) => {
    if (!projectName.trim()) return;

    const errors = [];

    // Check for uniqueness
    const existingTenders = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("mjEvans_tender_")) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            const tenderData = parsed.data || parsed;
            if (tenderData.projectName && tenderData.id !== state.id) {
              existingTenders.push(tenderData.projectName.toLowerCase());
            }
          } catch (e) {
            // Ignore invalid entries
          }
        }
      }
    }

    if (existingTenders.includes(projectName.toLowerCase())) {
      errors.push(
        "A tender with this project name already exists. Please choose a unique name.",
      );
    }

    setValidationErrors(errors);
  };

  const onSubmit = async (data: BasicTenderFormData) => {
    if (validationErrors.length === 0) {
      const hideLoading = message.loading({
        content: "Saving tender and creating folders...",
        key: "saveTender",
        duration: 0, // Keep loading message visible until manually closed
      });

      try {
        // Update form data first
        updateFormData(data);

        // Build updated state with form data
        const updatedState: TenderState = {
          ...state,
          projectName: data.projectName.trim(),
          client: data.client.trim(),
          siteAddress: (data.siteAddress || "").trim(),
          region: data.region,
          status: "In-progress",
          lastModified: new Date(),
        };

        // Save to local storage only (not calling saveTender which triggers duplicate API call)
        saveTenderToStorage(updatedState);

        // Prepare complete payload using the storage transformer
        const basePayload = prepareTenderForAPI(
          updatedState,
          user?.name || user?.email,
        );

        // Resolve opportunity number from local import state or context
        const resolvedOppNo = importedOpportunity?.no || state.opportunityNo;

        // Override/ensure critical fields are set correctly
        const completePayload = {
          ...basePayload,
          project: {
            name: data.projectName,
            client: data.client,
            region: data.siteAddress
              ? `${data.siteAddress}~~${data.region}`
              : data.region,
          },
          status: "In-Progress",
          lastModified: new Date().toISOString(),
          tenderCreatedDate:
            state.dateCreated?.toISOString() || new Date().toISOString(),
          calculatedAt: new Date().toISOString(),
          ...(resolvedOppNo && { opportunityNo: resolvedOppNo }),
        };

        // Submit to API (single API call with correct rate ID)
        await createBoqMutation.mutateAsync(completePayload);

        // Fire-and-forget: create site library so rate locks are in place from day one.
        // 201 = created, 409 = already existed — both are fine. Never blocks navigation.
        itemLibraryApi.createSite(data.region, {
          siteName: data.projectName,
          ...(resolvedOppNo && { oppNo: String(resolvedOppNo) }),
          createdBy: user?.email || user?.name,
        }).catch((err) => console.warn("⚠️ Site library creation failed (non-blocking):", err));

        // Create SharePoint folder for the project
        let folderCreationFailed = false;
        try {
          const folderResponse = await createFolder(
            data.projectName,
            resolvedOppNo,
          );
          if (
            !folderResponse.success &&
            folderResponse.error?.code !== "nameAlreadyExists"
          ) {
            console.warn(
              "SharePoint folder creation failed:",
              folderResponse.message,
            );
            folderCreationFailed = true;
          }
        } catch (folderError) {
          console.warn("SharePoint folder creation error:", folderError);
          folderCreationFailed = true;
        }

        // Close loading message and show result
        hideLoading();

        if (folderCreationFailed) {
          message.warning({
            content: "Tender saved but folder creation failed",
            duration: 3,
          });
        }

        // Redirect to job types selection
        setTimeout(() => {
          navigate("/job-types");
        }, 500);
      } catch (error: any) {
        console.error("❌ Error submitting tender:", error);
        console.error("Error response:", error.response?.data);
        hideLoading();
        message.error({
          content: `Failed to submit tender: ${
            error.response?.data?.message || error.message || "Unknown error"
          }`,
          duration: 5,
        });
      }
    }
  };

  const onSaveAndExit = async (data: BasicTenderFormData) => {
    if (validationErrors.length === 0) {
      const hideLoading = message.loading({
        content: "Saving tender and creating folders...",
        key: "saveTender",
        duration: 0, // Keep loading message visible until manually closed
      });

      try {
        // Update form data first
        updateFormData(data);

        // Build updated state with form data
        const updatedState: TenderState = {
          ...state,
          projectName: data.projectName.trim(),
          client: data.client.trim(),
          siteAddress: (data.siteAddress || "").trim(),
          region: data.region,
          status: "In-progress",
          lastModified: new Date(),
        };

        // Save to local storage only (not calling saveTender which triggers duplicate API call)
        saveTenderToStorage(updatedState);

        // Prepare complete payload using the storage transformer
        const basePayload = prepareTenderForAPI(
          updatedState,
          user?.name || user?.email,
        );

        // Resolve opportunity number from local import state or context
        const resolvedOppNo = importedOpportunity?.no || state.opportunityNo;

        // Override/ensure critical fields are set correctly
        const completePayload = {
          ...basePayload,
          project: {
            name: data.projectName,
            client: data.client,
            region: data.siteAddress
              ? `${data.siteAddress}~~${data.region}`
              : data.region,
          },
          status: "In-Progress",
          lastModified: new Date().toISOString(),
          tenderCreatedDate:
            state.dateCreated?.toISOString() || new Date().toISOString(),
          calculatedAt: new Date().toISOString(),
          ...(resolvedOppNo && { opportunityNo: resolvedOppNo }),
        };

        // Submit to API (single API call with correct rate ID)
        await createBoqMutation.mutateAsync(completePayload);

        // Fire-and-forget: create site library so rate locks are in place from day one.
        itemLibraryApi.createSite(data.region, {
          siteName: data.projectName,
          ...(resolvedOppNo && { oppNo: String(resolvedOppNo) }),
          createdBy: user?.email || user?.name,
        }).catch((err) => console.warn("⚠️ Site library creation failed (non-blocking):", err));

        // Create SharePoint folder for the project
        let folderCreationFailed = false;
        try {
          const folderResponse = await createFolder(
            data.projectName,
            resolvedOppNo,
          );
          if (
            !folderResponse.success &&
            folderResponse.error?.code !== "nameAlreadyExists"
          ) {
            console.warn(
              "SharePoint folder creation failed:",
              folderResponse.message,
            );
            folderCreationFailed = true;
          }
        } catch (folderError) {
          console.warn("SharePoint folder creation error:", folderError);
          folderCreationFailed = true;
        }

        // Close loading message and show result
        hideLoading();

        if (folderCreationFailed) {
          message.warning({
            content: "Tender saved but folder creation failed",
            duration: 3,
          });
        }

        // Redirect to home/dashboard
        setTimeout(() => {
          navigate("/");
        }, 500);
      } catch (error: any) {
        console.error("❌ Error submitting tender:", error);
        console.error("Error response:", error.response?.data);
        hideLoading();
        message.error({
          content: `Failed to submit tender: ${
            error.response?.data?.message || error.message || "Unknown error"
          }`,
          duration: 5,
        });
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const normalizeStatus = (status: string) => {
    const statusLower = status.toLowerCase();
    // Convert 'draft' to 'in-progress'
    if (statusLower === "draft") {
      return "in-progress";
    }
    return statusLower;
  };

  const getStatusColor = (status: string) => {
    const statusLower = (status || "").toLowerCase().trim();

    // New BOQ workflow statuses (matching dashboard)
    if (statusLower === "in-progress") return "blue";
    if (statusLower === "under review - senior estimator") return "orange";
    if (statusLower === "under review commercial director") return "gold";
    if (statusLower === "under client review") return "green";

    // Legacy statuses
    const statusNormalized = normalizeStatus(status);
    switch (statusNormalized) {
      case "approved":
        return "success";
      case "submitted":
        return "processing";
      case "pending":
        return "warning";
      case "won":
        return "success";
      case "lost":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusStyle = (status: string) => {
    return { fontWeight: 600, fontSize: "13px" };
  };

  const getStatusDisplay = (status: string) => {
    const statusNormalized = normalizeStatus(status);
    return toPascalCase(statusNormalized);
  };

  const toPascalCase = (str: string) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleLoadProjectFromList = () => {
    if (selectedProjectId) {
      const record = opportunities.find(
        (opp) => opp.tenderId === selectedProjectId,
      );
      if (record) {
        handleImportProject(record);
      }
    }
  };

  const canProceed =
    isValid &&
    Object.keys(errors).length === 0 &&
    validationErrors.length === 0 &&
    !isCreatingFolder;

  // Memoized callback for selecting project
  const handleSelectProject = useCallback((tenderId: string) => {
    setSelectedProjectId(tenderId);
  }, []);

  // Memoized render function for list items
  const renderOpportunityItem = useCallback(
    (project: OpportunityRecord) => {
      return (
        <OpportunityListItem
          key={project.tenderId}
          project={project}
          isSelected={selectedProjectId === project.tenderId}
          onSelect={handleSelectProject}
        />
      );
    },
    [selectedProjectId, handleSelectProject],
  );

  // Filter opportunities based on search query, region, and opp no
  const filteredOpportunities = useMemo(() => {
    let filtered = opportunities;

    // Only show In Progress, non-closed opportunities
    // Normalize XML hex-encoded spaces (_x0020_) that BC sometimes returns
    filtered = filtered.filter((opp) => {
      const normalizedStatus = (opp.status || '')
        .replace(/_x0020_/gi, ' ')
        .toLowerCase()
        .trim();
      return normalizedStatus === 'in progress' && opp.closed !== true;
    });

    // Filter by user's profile region (skip if "All Regions")
    if (userRegion && userRegion.trim().toLowerCase() !== 'all regions') {
      filtered = filtered.filter(
        (opp) => opp.region?.trim().toLowerCase() === userRegion.trim().toLowerCase()
      );
    }

    // Filter by search query (includes opp no, project name, client, site address)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((opp) => {
        return (
          opp.no?.toLowerCase().includes(query) ||
          opp.tender?.toLowerCase().includes(query) ||
          opp.client?.toLowerCase().includes(query) ||
          opp.siteAddress?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [opportunities, searchQuery, userRegion]);

  return (
    <MainLayout canProceed={canProceed} showFooter={false}>
      <div className="max-w-4xl mx-auto">
        {!isRoleLoading && role && role !== "Estimator" && (
          <Alert
            type="info"
            showIcon
            message="Authorized users have the ability to create tenders, while submitting and approving them is governed by role-based permissions assigned to Estimators, Senior Estimators, and the Commercial Director."
            className="mb-4"
          />
        )}
        <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <Title
              level={3}
              className="mb-1"
              style={{ fontWeight: 600, fontSize: "20px", color: "#111827" }}
            >
              Create new tender
            </Title>
            <Text style={{ color: "#9ca3af", fontSize: "14px" }}>
              Create or import tender details for your project
            </Text>
          </div>

          <div className="px-6 py-6">
            {/* Import Section */}
            <div className="mb-6">
              <Text
                className="block mb-3"
                style={{ color: "#000", fontSize: "14px" }}
              >
                {hasLocalStorageData
                  ? "Import existing opportunity."
                  : "Import an existing opportunity."}
              </Text>
              <div className="flex gap-3">
                <Button
                  onClick={handleOpenProjectSelect}
                  tabIndex={1}
                  onFocus={() => setFocusedBtn('import')}
                  onBlur={() => setFocusedBtn(null)}
                  style={{
                    backgroundColor: COLORS.primaryLight,
                    borderColor: focusedBtn === 'import' ? COLORS.primary : COLORS.primaryLight,
                    color: COLORS.primary,
                    fontWeight: 500,
                    outline: focusedBtn === 'import' ? `3px solid ${COLORS.primary}` : undefined,
                    outlineOffset: focusedBtn === 'import' ? '3px' : undefined,
                  }}
                  loading={opportunitiesLoading}
                >
                  Import Existing Opportunity
                </Button>
                {/* {hasLocalStorageData && (
                  <Button
                    onClick={handleLoadLocalProject}
                    style={{
                      backgroundColor: "#d1fae5",
                      borderColor: "#10b981",
                      color: "#047857",
                      fontWeight: 500,
                    }}
                  >
                    Load Draft
                  </Button>
                )} */}
              </div>
            </div>

            {isImportedData && importedOpportunity ? (
              <>
                <ImportedOpportunityDisplay
                  projectName={state.projectName}
                  client={state.client}
                  siteAddress={state.siteAddress}
                  region={state.region}
                  tenderId={importedOpportunity.tenderId}
                  status={importedOpportunity.status}
                  onCreateNew={handleCreateNewTender}
                />
              </>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Form Section */}
                <div className="mb-6">
                  <Title
                    level={5}
                    className="mb-4"
                    style={{
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "#111827",
                    }}
                  >
                    Create a new tender
                  </Title>

                  <Row gutter={[24, 24]}>
                    {/* Project Name */}
                    <Col xs={24} md={12}>
                      <div className="space-y-1">
                        <Text
                          className="block"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#374151",
                            marginBottom: "6px",
                          }}
                        >
                          Tender Name{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </Text>
                        <Controller
                          name="projectName"
                          control={control}
                          rules={{
                            required: "Project name is required",
                            minLength: {
                              value: 3,
                              message:
                                "Project name must be at least 3 characters",
                            },
                            validate: (value) =>
                              value.trim().length > 0 ||
                              "Project name cannot be blank",
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              tabIndex={2}
                              placeholder="Enter project name"
                              status={errors.projectName ? "error" : undefined}
                              onBlur={() => {
                                field.onBlur();
                                validateProjectName(field.value);
                              }}
                            />
                          )}
                        />
                        {errors.projectName && (
                          <Text type="danger" className="text-xs">
                            {errors.projectName.message}
                          </Text>
                        )}
                      </div>
                    </Col>

                    {/* Client Name */}
                    <Col xs={24} md={12}>
                      <div className="space-y-1">
                        <Text
                          className="block"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#374151",
                            marginBottom: "6px",
                          }}
                        >
                          Client Name{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </Text>
                        <Controller
                          name="client"
                          control={control}
                          rules={{
                            required: "Client name is required",
                            validate: (value) =>
                              value.trim().length > 0 ||
                              "Client name cannot be blank",
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              tabIndex={3}
                              placeholder="Enter client name"
                              status={errors.client ? "error" : undefined}
                              onBlur={() => field.onBlur()}
                            />
                          )}
                        />
                        {errors.client && (
                          <Text type="danger" className="text-xs">
                            {errors.client.message}
                          </Text>
                        )}
                      </div>
                    </Col>

                    {/* Site Address */}
                    <Col xs={24} md={12}>
                      <div className="space-y-1">
                        <Text
                          className="block"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#374151",
                            marginBottom: "6px",
                          }}
                        >
                          Site Address{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </Text>
                        <Controller
                          name="siteAddress"
                          control={control}
                          rules={{
                            required: "Site address is required",
                            validate: (value) =>
                              value.trim().length > 0 ||
                              "Site address cannot be blank",
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              tabIndex={4}
                              placeholder="Enter site address"
                              status={errors.siteAddress ? "error" : undefined}
                              onBlur={() => field.onBlur()}
                            />
                          )}
                        />
                        {errors.siteAddress && (
                          <Text type="danger" className="text-xs">
                            {errors.siteAddress.message}
                          </Text>
                        )}
                      </div>
                    </Col>

                    {/* Region */}
                    <Col xs={24} md={12}>
                      <div className="space-y-1">
                        <Text
                          className="block"
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#374151",
                            marginBottom: "6px",
                          }}
                        >
                          Region <span style={{ color: "#ef4444" }}>*</span>
                        </Text>
                        <Controller
                          name="region"
                          control={control}
                          rules={{ required: "Region is required" }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={field.value || undefined}
                              placeholder="Select region"
                              className="w-full"
                              tabIndex={5}
                              status={errors.region ? "error" : undefined}
                              disabled={
                                !!(userRegion && userRegion !== "All Regions")
                              }
                              options={REGIONS.map((region) => ({
                                label: region,
                                value: region,
                              }))}
                            />
                          )}
                        />
                        {errors.region && (
                          <Text type="danger" className="text-xs">
                            {errors.region.message}
                          </Text>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <Alert
                    type="error"
                    className="mb-6"
                    message={
                      <div>
                        <Text className="font-semibold">
                          Please fix the following errors:
                        </Text>
                        <ul className="mt-2 ml-4 list-disc">
                          {validationErrors.map((error, index) => (
                            <li key={index}>
                              <Text className="text-sm">{error}</Text>
                            </li>
                          ))}
                        </ul>
                      </div>
                    }
                  />
                )}

                {/* Date Information - only show if project has been created/saved */}
                {state.projectName && state.projectName.trim() !== "" && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarOutlined
                        style={{ color: "#6b7280", fontSize: "14px" }}
                      />
                      <Text style={{ color: "#6b7280", fontSize: "14px" }}>
                        Date Created:{" "}
                        <span style={{ fontWeight: 600, color: "#374151" }}>
                          {format(state.dateCreated, "MMMM do, yyyy h:mm a")}
                        </span>
                      </Text>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarOutlined
                        style={{ color: "#6b7280", fontSize: "14px" }}
                      />
                      <Text style={{ color: "#6b7280", fontSize: "14px" }}>
                        Last Modified:{" "}
                        <span style={{ fontWeight: 600, color: "#374151" }}>
                          {format(state.lastModified, "MMMM do, yyyy h:mm a")}
                        </span>
                      </Text>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Action Buttons Section */}
          <div style={{ borderTop: "1px solid #e5e7eb" }}>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button
                onClick={canProceed ? handleSubmit(onSaveAndExit) : undefined}
                tabIndex={6}
                style={!canProceed ? {
                  borderColor: '#d9d9d9',
                  color: 'rgba(0,0,0,0.25)',
                  backgroundColor: '#fff',
                  cursor: 'not-allowed',
                } : {
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                }}
              >
                Save and Exit
              </Button>
              <Button
                type="primary"
                htmlType="button"
                onClick={canProceed ? handleSubmit(onSubmit) : undefined}
                tabIndex={7}
                style={!canProceed ? {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  borderColor: '#d9d9d9',
                  color: 'rgba(0,0,0,0.25)',
                  cursor: 'not-allowed',
                } : {
                  backgroundColor: COLORS.primary,
                  borderColor: COLORS.primary,
                }}
              >
                Save & Continue <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </Card>

        {/* Project Selection Modal */}
        <Modal
          open={projectSelectModalVisible}
          onCancel={handleCancelProjectSelect}
          width={800}
          title={
            <Typography.Title level={4} className="text-blue-900 mb-0">
              Import Client Details
            </Typography.Title>
          }
          footer={
            <div className="flex justify-end gap-2">
              <Button
                onClick={handleCancelProjectSelect}
                className="text-gray-600 hover:text-gray-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLoadProjectFromList}
                type="primary"
                disabled={!selectedProjectId || isImporting}
                className="bg-blue-900 [&:not(:disabled):hover]:bg-blue-800 border-blue-900"
              >
                Import Client Details
              </Button>
            </div>
          }
        >
          <div className="py-4">
            {apiError && (
              <Alert
                message="API Error"
                description={apiError}
                type="warning"
                showIcon
                closable
                onClose={() => setApiError(null)}
                className="mb-4"
              />
            )}
            {isOpportunitiesLoading || (!!bcOpportunities?.length && opportunities.length === 0) ? (
              <div
                className="flex flex-col items-center justify-center gap-3"
                style={{ padding: "48px 0" }}
              >
                <Spin size="large" />
                <Text style={{ color: "#6b7280" }}>Loading opportunities...</Text>
              </div>
            ) : (
              <div>
                {!apiError && opportunities.length > 0 && (
                  <>
                    <Text className="mb-3 text-gray-600 block">
                      Select an opportunity to load and continue working on it:
                    </Text>
                    <Input
                      placeholder="Search by opportunity no., project name, client, or site address..."
                      prefix={<SearchOutlined className="text-gray-400" />}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      allowClear
                      className="mb-4"
                      size="large"
                    />
                  </>
                )}

                <div className="border border-gray-200 rounded-lg max-h-96 overflow-auto">
                  <List
                    dataSource={filteredOpportunities}
                    renderItem={renderOpportunityItem}
                    locale={{
                      emptyText: searchQuery
                        ? `No opportunities found matching "${searchQuery}"`
                        : "No opportunities available",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default TenderInfo;
